/* eslint-disable react/no-danger */
import React, { useRef } from 'react';
import { Field } from 'react-final-form';
import { get } from 'lodash';
import moment from 'moment';
import format from 'date-fns/format';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { AddOnBlock, Heading, Box } from '@landtrustinc/design-system';

import { required, composeValidators } from '../../../util/validators';
import { getLodgingIcon } from '../../../util/lodging';

import InquiryWizardPanel from '../InquiryWizardPanel';
import InquiryWizardSection from '../InquiryWizardSection';
import { FieldDates, FieldPartySizeSelect, FlexiblePartySizeSelect } from '../..';
import { ReactComponent as CheckIcon } from '../../../assets/icons/trust-check.svg';
import { daysAdded, getDaysFromTimeslots } from '../../../util/dates';
import {
  getPackageLodgingPrices,
  handleLodgingAddOnChange,
  isFlexiblePricingEnabled,
} from '../../../util/package';
import { getLodgingPricesOptions } from '../../../util/listing';

import css from './InquiryWizardStepParty.module.css';

const InquiryWizardStepParty = ({ intl, listing, timeSlots, formValues, onChange, form }) => {
  const { selectedPackage } = formValues || {};
  const fieldDatesContainerRef = useRef(null);

  const { guests, price, hasLodging } = selectedPackage || {};
  const { max: maxParty, min: minParty = 1 } = { ...guests };
  const maxDiyPartySize = get(listing, 'attributes.publicData.policies.sportsmen', 1);

  const startDate = get(formValues, 'bookingDates.startDate');
  const endDate = get(formValues, 'bookingDates.endDate');
  const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
  const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

  let bookingDateText = intl.formatMessage({ id: 'InquiryWizard.datesFieldPlaceholder' });
  const dateValues = formValues && formValues.bookingDates;

  const isSingleDay =
    daysAdded(
      dateValues && formValues.bookingDates.startDate,
      dateValues && formValues.bookingDates.endDate
    ) === 1;

  if (dateValues) {
    bookingDateText = `${format(formValues.bookingDates.startDate, 'MMM DD')} - ${format(
      formValues.bookingDates.endDate,
      'MMM DD, YYYY'
    )}`;
  }

  if (dateValues && isSingleDay) {
    bookingDateText = format(
      formValues && formValues.bookingDates && formValues.bookingDates.startDate,
      'MMM DD'
    );
  }

  const availableDays = getDaysFromTimeslots(timeSlots);
  const lodgingPricesOptions = getLodgingPricesOptions(listing, intl);
  const { optionalLodging } = getPackageLodgingPrices(selectedPackage, lodgingPricesOptions);

  // Calculate dynamic hasLodging similar to PackagePricingPanel
  const includeLodgings = formValues?.lodgingAddons?.length > 0 || hasLodging;
  const tripLength = startDate && endDate ? daysAdded(startDate, endDate) : 0;
  const dynamicHasLodging = includeLodgings && tripLength > 1;

  let calendarInfoDays;
  const { days } = selectedPackage || {};

  if (days.min === days.max) {
    calendarInfoDays = days.min;
  } else if (days.max === 365) {
    calendarInfoDays = `${days.min}+`;
  } else {
    calendarInfoDays = `${days.min}-${days.max}`;
  }

  return (
    <InquiryWizardPanel className={css.panelContainer}>
      <InquiryWizardSection>
        <div className={css.fieldContainer}>
          {isFlexiblePricingEnabled(selectedPackage?.activity) ? (
            <FlexiblePartySizeSelect
              id="party-size-select"
              className={css.partySizeSelect}
              name="partySize"
              minPartySize={minParty}
              maxPartySize={maxParty || maxDiyPartySize}
              price={price}
              packageFlexiblePricing={selectedPackage?.flexiblePricing}
              validate={composeValidators(
                required(
                  intl.formatMessage({
                    id: 'InquiryWizard.partySizeRequired',
                  })
                )
              )}
              readOnlyInput
              label={<FormattedMessage id="PricingPage.partySizeLabel" />}
            />
          ) : (
            <FieldPartySizeSelect
              id="party-size-select"
              className={css.partySizeSelect}
              name="partySize"
              minPartySize={minParty || 1}
              maxPartySize={maxParty || maxDiyPartySize}
              price={price}
              parse={value => (value === '' ? null : Number(value))}
              format={value => String(value)}
              validate={required(
                intl.formatMessage({
                  id: 'InquiryWizard.partySizeRequired',
                })
              )}
              label={<FormattedMessage id="PricingPage.partySizeLabel" />}
            />
          )}
        </div>

        <div>
          <div className={css.formFieldWrapper} ref={fieldDatesContainerRef}>
            <FieldDates
              fieldDateProps={{
                endDateOffset:
                  selectedPackage.days.min === selectedPackage.days.max
                    ? day => day.add(selectedPackage.days.min - 1, 'days')
                    : undefined,
                minimumNights:
                  selectedPackage.days.min !== selectedPackage.days.max &&
                  selectedPackage.days.min - 1,
                maximumNights:
                  selectedPackage.days.max > 0 &&
                  selectedPackage.days.min !== selectedPackage.days.max
                    ? selectedPackage.days.max - 1
                    : undefined,
                calendarInfo: (
                  <FormattedMessage
                    id="PricingPage.datesFieldInfoPackageTitle"
                    values={{ days: calendarInfoDays }}
                  />
                ),
                hasLodging: hasLodging || dynamicHasLodging,
                containerRef: fieldDatesContainerRef,
                activity: selectedPackage.activity,
              }}
              timeSlots={timeSlots}
              label={<FormattedMessage id="InquiryWizard.datesFieldHeadingPackage" />}
              text={bookingDateText}
              onChange={values => {
                onChange(values);
              }}
              isPackage
              placement="bottom-end"
            />
            {startDate && endDate && (
              <div className={css.availableDates}>
                <CheckIcon />
                <FormattedMessage
                  id="InquiryWizard.selectedDate"
                  values={{ dates: formattedStartDate === formattedEndDate ? 1 : 0 }}
                />
              </div>
            )}
          </div>
        </div>
        {optionalLodging?.length > 0 && (selectedPackage.days.min > 1 || !isSingleDay) && (
          <div className={css.formFieldWrapper}>
            <div className={css.packageLodgingAddOn}>
              <Box mb="var(--spacing-3)">
                <Heading size="2xs" fontWeight="bold">
                  <FormattedMessage id="InquiryWizard.addOnsTitle" />
                </Heading>
              </Box>

              <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
                {optionalLodging.map((item, index) => {
                  const uniqueKey = `${item.type}-${item.index || index}`;
                  return (
                    <Field key={uniqueKey} name="lodgingAddons" type="checkbox" value={index}>
                      {({ input }) => {
                        return (
                          <AddOnBlock
                            title={item.shortLabel}
                            subtitle={`+ ${item.priceLabel}`}
                            icon={getLodgingIcon(item.type)}
                            checked={input.checked}
                            onClick={() => {
                              const currentLodgingAddons = formValues.lodgingAddons || [];
                              const updatedAddons = handleLodgingAddOnChange(
                                index,
                                currentLodgingAddons,
                                dateValues,
                                availableDays,
                                hasLodging,
                                form.change
                              );
                              form.change('lodgingAddons', updatedAddons);
                            }}
                          />
                        );
                      }}
                    </Field>
                  );
                })}
              </Box>
            </div>
          </div>
        )}
      </InquiryWizardSection>
    </InquiryWizardPanel>
  );
};

InquiryWizardStepParty.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(InquiryWizardStepParty);
